import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Label, LanguageService } from '@services/public';
import {
	HTTP_CACHE_MAX_AGE,
	HTTP_REQUEST_KEY,
	httpExpireRequests,
} from '@util';
import { config } from 'environment';
import { ExposedPromise } from 'jakapa-utilities-lite';
import { Observable } from 'rxjs';

// Cache expiration in milliseconds.
const CACHE_EXPIRATION = 300000;

// Group metadata
export interface Group {
	uuid: string;
	name: string;
	abbreviation?: string;
	timezone?: string | null;
	startDate?: string;
	endDate?: string | null;
	organizationUUID?: string;
	member?: boolean;
	users?: Array<any>;
	role?: string | null;
	memberUUIDs?: Array<string>;
}

export const ALL_GROUPS: Group = {
	uuid: null,
	name: 'All Groups',
};

export const NO_GROUPS: Group = {
	uuid: null,
	name: 'No Groups',
};

@Injectable({
	providedIn: 'root',
})
export class GroupService {
	private readonly _key: symbol = Symbol();

	// Page langauge.
	labels: { [key in Label]?: string } = {
		[Label.ALL_GROUPS]: '',
		[Label.NO_GROUPS]: '',
	};

	constructor(
		private _http: HttpClient,
		private _languageSvc: LanguageService,
	) {
		// Get page language.
		this._languageSvc.getLabels(this.labels);
		ALL_GROUPS.name = this.labels[Label.ALL_GROUPS];
		NO_GROUPS.name = this.labels[Label.NO_GROUPS];
	}

	clearCache(): void {
		httpExpireRequests(this._key);
	}

	/**
	 * Retrieves a list of all the groups the user can see. Cached. Always
	 * returns an array with at least 1 group. If there is an error or if no
	 * group matches the filter, then an array with either the NO_GROUPS or the
	 * ALL_GROUPS groups will be returned.
	 *
	 * @param allGroups If the ALL_GROUPS group should be added to the front of
	 * 	the array. Defaults to false.
	 * @param filter A filter function for the groups.
	 *
	 * @returns An array of groups.
	 */
	getGroups(
		allGroups: boolean = false,
		filter: (group: Group) => boolean = () => true,
	): Promise<Array<Group>> {
		const promise = new ExposedPromise<Array<Group>>();
		this._http
			.get<Array<Group>>(`${config.apiBase}member/group/list`, {
				context: new HttpContext()
					.set(HTTP_REQUEST_KEY, this._key)
					.set(HTTP_CACHE_MAX_AGE, CACHE_EXPIRATION),
			})
			.subscribe({
				next: (res: Array<Group>): void => {
					const groups = res.filter(filter);
					if (allGroups) groups.unshift(ALL_GROUPS);
					if (groups.length === 0) groups.push(NO_GROUPS);
					promise.resolve(groups);
				},
				error: (): void => {
					if (allGroups) promise.resolve([ALL_GROUPS]);
					else promise.resolve([NO_GROUPS]);
				},
			});
		return promise.toPromise();
	}

	/**
	 * Retrieves a list of all the groups the user has access to. Cached.
	 *
	 * @returns {Array<Group>} A list of groups.
	 */
	getGroupsAPI(): Observable<Array<Group>> {
		return this._http.get<Array<Group>>(`${config.apiBase}member/group/list`, {
			context: new HttpContext()
				.set(HTTP_REQUEST_KEY, this._key)
				.set(HTTP_CACHE_MAX_AGE, CACHE_EXPIRATION),
		});
	}

	registerGroup(groupCode: string): Observable<any> {
		return this._http.post<any>(`${config.apiBase}user/group/register`, {
			groupCode,
		});
	}

	leaveGroup(reqObj): Observable<any> {
		return this._http.delete<any>(`${config.apiBase}member/group/leave`, {
			body: reqObj,
		});
	}

	sendGroupRequest(requredObj) {
		return this._http.post<any>(
			`${config.apiBase}member/group/join`,
			requredObj,
		);
	}
}
